import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Sparkles, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";

export default function AdminIngest() {
  const [useGov, setUseGov] = useState(true);
  const [useNews, setUseNews] = useState(true);
  const [maxDrafts, setMaxDrafts] = useState(5);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (dryRun) => {
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/admin/ingest/run", null, {
        params: { use_government: useGov, use_news: useNews, max_drafts: maxDrafts, dry_run: dryRun },
      });
      setResult(r.data);
      if (dryRun) toast.success(`Dry run: ${r.data.new_unseen} new candidates found`);
      else toast.success(`${r.data.drafted} new draft(s) created`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ingest failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 font-mono-plex text-sm uppercase tracking-wider hover:underline" data-testid="back-to-admin">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>

      <div className="mt-4 border-b border-black/15 pb-6">
        <div className="overline">/automation</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-2">Daily ingest</h1>
        <p className="text-base text-[#333] mt-3 max-w-2xl leading-relaxed">
          Pulls candidate URLs from configured government + news feeds, filters for
          women's-rights keywords, and creates <b>drafts only</b>. Cron runs daily at 13:00 UTC.
          Use the controls below to trigger an extra run on demand.
        </p>
      </div>

      <div className="mt-8 border-2 border-black bg-white p-6">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          <h2 className="font-display text-2xl font-bold">Run controls</h2>
        </div>

        <div className="mt-4 space-y-3 max-w-md">
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox checked={useGov} onCheckedChange={(v) => setUseGov(!!v)} className="rounded-none border-black" data-testid="ingest-use-gov" />
            <span>Government feeds (Congress.gov, Federal Register, SCOTUS)</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox checked={useNews} onCheckedChange={(v) => setUseNews(!!v)} className="rounded-none border-black" data-testid="ingest-use-news" />
            <span>News feeds (Reuters, AP, Guttmacher, CAP)</span>
          </label>
          <label className="block">
            <span className="overline mb-1.5 block">Max drafts per run</span>
            <input
              type="number"
              value={maxDrafts}
              onChange={(e) => setMaxDrafts(parseInt(e.target.value, 10) || 1)}
              min="1"
              max="50"
              data-testid="ingest-max-drafts"
              className="w-full rounded-none border border-black/30 h-11 px-3"
            />
          </label>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={() => run(true)} disabled={running} variant="outline" className="rounded-none border-black h-11" data-testid="ingest-dry-run">
            {running ? "Running…" : "Dry run (preview)"}
          </Button>
          <Button onClick={() => run(false)} disabled={running} className="rounded-none bg-black text-white h-11" data-testid="ingest-run">
            {running ? "Running…" : "Create drafts now"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="mt-6 border border-black/15 bg-white p-5" data-testid="ingest-result">
          <div className="overline">/result</div>
          <div className="mt-2 grid grid-cols-3 gap-px bg-black/10">
            <Stat label="Candidates" value={result.total_candidates} />
            <Stat label="New (unseen)" value={result.new_unseen} />
            <Stat label="Drafted" value={result.drafted} accent={result.drafted > 0 ? "success" : null} />
          </div>
          {result.dry_run && <div className="mt-3 text-sm text-[#555]">Dry run — no drafts were created.</div>}
          {result.drafts && result.drafts.length > 0 && (
            <div className="mt-4">
              <div className="overline mb-2">/{result.dry_run ? "would-draft" : "drafts created"}</div>
              <ul className="space-y-2">
                {result.drafts.map((d, i) => (
                  <li key={d.id || d.url || i} className="border border-black/15 p-3 text-sm">
                    <div className="font-display font-bold leading-snug">{d.title}</div>
                    <div className="font-mono-plex text-[11px] text-[#555] mt-1 break-all">{d.url}</div>
                    {d.id && (
                      <Link to={`/admin/edit/${d.id}`} className="inline-flex items-center gap-1.5 mt-2 text-xs underline" data-testid={`open-draft-${d.id}`}>
                        <FileText className="w-3.5 h-3.5" /> Open draft
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 border border-amber-400 bg-amber-50 p-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5" />
          <p className="text-sm text-amber-900">
            Drafts are tagged <code>daily-ingest, needs-review</code>. They are NEVER published automatically —
            every entry still goes through the same human-review pipeline.
          </p>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  let color = "text-black";
  if (accent === "success") color = "text-[#008A00]";
  return (
    <div className="bg-white p-3">
      <div className="font-mono-plex text-[11px] uppercase tracking-wider text-[#555]">{label}</div>
      <div className={`font-display text-3xl font-black mt-1 ${color}`}>{value}</div>
    </div>
  );
}
